import * as TabsPrimitive from '@radix-ui/react-tabs'
import classNames from 'classnames'
import React, { ReactElement } from 'react'
import { useTheme } from '@opallabs/hooks'
import { Button } from '../Button'
import type { Variant } from './styles'
import * as styles from './styles'

export { selectors as tabsSelectors } from './styles'

type TabsRootElement = React.ElementRef<typeof TabsPrimitive.Root>
type TabsRootProps = Omit<
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Root>,
  'orientation'
>

/**
 * Tabs component based on the Radix UI primitive Tabs component. The `orientation`
 * props is not implemented here and has been removed. `List` and `Trigger` sub-components
 * have had the `asChild` prop removed and are meant to be used as configured.
 *
 * For more info see the Radix UI documentation: https://www.radix-ui.com/primitives/docs/components/tabs
 *
 * @note This component isn’t formally deprecated, but you SHOULD NOT use it. Instead, depending on the semantics of
 * your use case, you should use `<SegmentedControl>`, `<ViewTabs>`, `<SidebarTabs>`, or `<QuasiTabs>`.
 */
export const Root = React.forwardRef<TabsRootElement, TabsRootProps>(
  (props, forwardedRef) => {
    const { className, ...otherProps } = props
    return (
      <TabsPrimitive.Root
        {...otherProps}
        className={classNames(className, styles.selectors.root)}
        ref={forwardedRef}
      />
    )
  }
)
Root.displayName = 'Lapidary: Tabs.Root'

export const Content = TabsPrimitive.Content
Content.displayName = 'Lapidary: Tabs.Content'

type ListSize = 'default' | 'small'

type TabsListElement = React.ElementRef<typeof TabsPrimitive.List>

interface TabsListProps
  extends Omit<
    React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>,
    'asChild'
  > {
  size?: ListSize
  variant?: Variant
}

export const List = React.forwardRef<TabsListElement, TabsListProps>(
  (props, forwardedRef) => {
    const theme = useTheme()
    const {
      className,
      size = 'default',
      variant = 'tertiary',
      ...otherProps
    } = props
    return (
      <TabsPrimitive.List
        {...otherProps}
        className={classNames(styles.selectors.list, className)}
        css={styles.list({ theme, variant })}
        ref={forwardedRef}
        data-size={size}
      />
    )
  }
)

List.displayName = 'Lapidary: Tabs.List'

type TabsTriggerElement = React.ElementRef<typeof TabsPrimitive.Trigger>

type TabsTriggerProps = Omit<
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>,
  'asChild'
> & {
  buttonIcon?: ReactElement
}

export const Trigger = React.forwardRef<TabsTriggerElement, TabsTriggerProps>(
  (props, forwardedRef) => {
    const { buttonIcon, children, className, value, ...otherProps } = props
    const theme = useTheme()

    return (
      <TabsPrimitive.Trigger
        {...otherProps}
        className={classNames(styles.selectors.trigger, className)}
        value={value}
        asChild
        ref={forwardedRef}
      >
        <Button
          css={styles.trigger}
          name={value}
          icon={buttonIcon}
          variant="ghost"
          theme={theme}
          aria-controls={value}
        >
          {children}
        </Button>
      </TabsPrimitive.Trigger>
    )
  }
)

Trigger.displayName = 'Lapidary: Tabs.Trigger'
